import { ReactNode, createContext, useContext, useMemo, useReducer } from "react";

import { EntityKind } from "models/entityKind";

import { FilterKey } from "components/map/toolbar/midstream-navigator/FilterButton";

export type MidstreamNavigatorMode = EntityKind;

export interface MidstreamNavigatorState {
  mode: MidstreamNavigatorMode;
  activeFilters: FilterKey[] | null;
  highlightActive: boolean;
  minContributionPercent: number | null;
  selectedMonth: string | null;
}

// Action type constants
export const MIDSTREAM_NAVIGATOR_ACTIONS = {
  SET_MODE: "SET_MODE",
  SET_ACTIVE_FILTER: "SET_ACTIVE_FILTER",
  CLEAR_FILTERS: "CLEAR_FILTERS",
  TOGGLE_HIGHLIGHT: "TOGGLE_HIGHLIGHT",
  SET_MIN_CONTRIBUTION_PERCENT: "SET_MIN_CONTRIBUTION_PERCENT",
  SET_SELECTED_MONTH: "SET_SELECTED_MONTH",
  ADD_FILTER: "ADD_FILTER",
  REMOVE_FILTER: "REMOVE_FILTER",
  TOGGLE_FILTER: "TOGGLE_FILTER"
} as const;

export type MidstreamNavigatorAction =
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.SET_MODE; mode: MidstreamNavigatorMode }
  | {
      type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.SET_ACTIVE_FILTER;
      filterKey: FilterKey | null;
    }
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.CLEAR_FILTERS }
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_HIGHLIGHT; active: boolean }
  | {
      type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.SET_MIN_CONTRIBUTION_PERCENT;
      minContributionPercent: number | null;
    }
  | {
      type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.SET_SELECTED_MONTH;
      selectedMonth: string | null;
    }
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.ADD_FILTER; filterKey: FilterKey }
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.REMOVE_FILTER; filterKey: FilterKey }
  | { type: typeof MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_FILTER; filterKey: FilterKey };

// Action creators
export const midstreamNavigatorActions = {
  setMode: (mode: MidstreamNavigatorMode): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.SET_MODE,
    mode
  }),
  setActiveFilter: (filterKey: FilterKey | null): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.SET_ACTIVE_FILTER,
    filterKey
  }),
  clearFilters: (): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.CLEAR_FILTERS
  }),
  toggleHighlight: (active: boolean): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_HIGHLIGHT,
    active
  }),
  setMinContributionPercent: (
    minContributionPercent: number | null
  ): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.SET_MIN_CONTRIBUTION_PERCENT,
    minContributionPercent
  }),
  setSelectedMonth: (selectedMonth: string | null): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.SET_SELECTED_MONTH,
    selectedMonth
  }),
  addFilter: (filterKey: FilterKey): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.ADD_FILTER,
    filterKey
  }),
  removeFilter: (filterKey: FilterKey): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.REMOVE_FILTER,
    filterKey
  }),
  toggleFilter: (filterKey: FilterKey): MidstreamNavigatorAction => ({
    type: MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_FILTER,
    filterKey
  })
};
const initialMidstreamNavigatorState: MidstreamNavigatorState = {
  mode: EntityKind.Facility,
  activeFilters: null,
  highlightActive: false,
  minContributionPercent: null,
  selectedMonth: null
};

function midstreamNavigatorReducer(
  state: MidstreamNavigatorState,
  action: MidstreamNavigatorAction
): MidstreamNavigatorState {
  switch (action.type) {
    case MIDSTREAM_NAVIGATOR_ACTIONS.ADD_FILTER:
      if (state.activeFilters?.includes(action.filterKey)) return state; // No duplicates
      return {
        ...state,
        activeFilters: state.activeFilters
          ? [...state.activeFilters, action.filterKey]
          : [action.filterKey]
      };
    case MIDSTREAM_NAVIGATOR_ACTIONS.REMOVE_FILTER:
      if (state.activeFilters) {
        const updatedFilters = state.activeFilters.filter(
          (key) => key !== action.filterKey
        );
        return {
          ...state,
          activeFilters: updatedFilters.length > 0 ? updatedFilters : null
        };
      }
      return state;

    case MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_FILTER: {
      if (!action.filterKey) return state;

      const currentFilters = state.activeFilters || [];

      if (currentFilters.includes(action.filterKey)) {
        const remaining = currentFilters.filter((key) => key !== action.filterKey);
        return { ...state, activeFilters: remaining.length > 0 ? remaining : null };
      } else {
        return { ...state, activeFilters: [...currentFilters, action.filterKey] };
      }
    }
    case MIDSTREAM_NAVIGATOR_ACTIONS.SET_MODE:
      return { ...state, mode: action.mode, activeFilters: null, highlightActive: false }; // Clear filters when changing mode
    case MIDSTREAM_NAVIGATOR_ACTIONS.SET_ACTIVE_FILTER:
      return { ...state, activeFilters: action.filterKey ? [action.filterKey] : null };
    case MIDSTREAM_NAVIGATOR_ACTIONS.CLEAR_FILTERS:
      return { ...state, activeFilters: null };
    case MIDSTREAM_NAVIGATOR_ACTIONS.TOGGLE_HIGHLIGHT:
      return { ...state, highlightActive: action.active };
    case MIDSTREAM_NAVIGATOR_ACTIONS.SET_MIN_CONTRIBUTION_PERCENT:
      return { ...state, minContributionPercent: action.minContributionPercent };
    case MIDSTREAM_NAVIGATOR_ACTIONS.SET_SELECTED_MONTH:
      return { ...state, selectedMonth: action.selectedMonth };
    default:
      return state;
  }
}

type MidstreamNavigatorContextT = [
  MidstreamNavigatorState,
  React.Dispatch<MidstreamNavigatorAction>
];

const MidstreamNavigatorContext = createContext<MidstreamNavigatorContextT | undefined>(
  undefined
);
MidstreamNavigatorContext.displayName = "MidstreamNavigatorContext";

export const MidstreamNavigatorProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(
    midstreamNavigatorReducer,
    initialMidstreamNavigatorState
  );
  const value = useMemo(() => [state, dispatch] as MidstreamNavigatorContextT, [state]);
  return (
    <MidstreamNavigatorContext.Provider value={value}>
      {children}
    </MidstreamNavigatorContext.Provider>
  );
};

export const useMidstreamNavigatorContext = () => {
  const ctx = useContext(MidstreamNavigatorContext);
  if (!ctx)
    throw new Error(
      "MidstreamNavigatorContext must be used within MidstreamNavigatorProvider"
    );
  return ctx;
};
